/**
 * `<SnapshotAsOfView/>` — render an opened snapshot as the read-only as-of
 * tree at its pinned instant (issue #765, B3.4).
 *
 * Opening a snapshot IS rewinding to its `at_timestamp` (§A4.1): the backend
 * runs the SAME `project_as_of` the rewind (B3.3, #764) uses, so this surface
 * reuses the #764 temporal infra verbatim — `adaptAsOfNodes` → `buildTree` →
 * the read-only `<TaskCanvas/>`. We deliberately do NOT fork the rewind view
 * (it owns a scrubber + day timeline this surface doesn't want); we reuse its
 * adapter + canvas-in-read-only-mode, which is the load-bearing reuse.
 *
 * Composition, top to bottom:
 *   1. A header naming the snapshot — its label + the instant it captures + a
 *      "← Back to snapshots" affordance.
 *   2. A factual "as of <date>" banner (same calm framing as the rewind).
 *   3. The read-only canvas, fed the snapshot's node set.
 *
 * Graceful degradation: a 404 / error renders a calm "couldn't open" state;
 * an empty node set renders the origin/empty state. Never a crash.
 *
 * Brand firewall (§A9): a snapshot is a bookmark of the PLAN's state at an
 * instant. The tree is read-only context; there is no per-person activity
 * overlay. `created_by` provenance lives on the LIST row, not here.
 *
 * Isolation: taskmap-local imports only — reuses the rewind adapter + the
 * shared read-only canvas; no scheduler coupling.
 */

import { useMemo } from "react";

import type { TaskSnapshot } from "../api/useTaskSnapshots";
import { useOpenSnapshot } from "../api/useTaskSnapshots";
import { TaskCanvas } from "../components/TaskCanvas";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { adaptAsOfNodes } from "../rewind/asOfAdapter";
import { formatDayLabel } from "../rewind/rewindTimeline";
import { useMapTheme } from "../theme/MapThemeContext";
import { buildTree } from "../tree/buildTree";
import { PALETTE } from "../tree/palette";

const AMBER_BG = "#FEF3C7";
const AMBER_INK = "#B45309";

/** Format a snapshot's pinned instant as a calm "May 12" label. */
function snapshotDateLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : formatDayLabel(d);
}

export interface SnapshotAsOfViewProps {
  teamId: string | undefined;
  /** The snapshot to open (its id drives the open query). */
  snapshot: TaskSnapshot;
  /** The team's roster, for assignee avatars (shared with the live editor). */
  members: TaskMember[];
  /** Return to the snapshot list. */
  onBack: () => void;
}

export function SnapshotAsOfView(props: SnapshotAsOfViewProps) {
  const { teamId, snapshot, members, onBack } = props;
  const { nodes, isLoading, isError } = useOpenSnapshot(teamId, snapshot.id);

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // Reuse the rewind's adapter → the same canvas pipeline (drop archived-then).
  const roots = useMemo(() => buildTree(adaptAsOfNodes(nodes)), [nodes]);
  const hasNodes = roots.length > 0;
  const dateLabel = snapshotDateLabel(snapshot.at_timestamp);

  return (
    <div data-testid="snapshot-as-of-view" className="relative flex h-[calc(100vh-8rem)] flex-col">
      {/* Header: the snapshot's name + instant + back. Editorial re-skins the
          name to serif-italic + the instant to a mono neutral chip; legacy
          keeps the bold name + amber badge (byte-identical). */}
      <div
        className="flex items-center justify-between gap-3 border-b px-4 py-2"
        style={{
          borderColor: editorial ? c.rule : PALETTE.softBorder,
          backgroundColor: editorial ? c.cardBg : PALETTE.canvas,
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            data-testid="snapshot-back"
            onClick={onBack}
            className="rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-slate-50"
            style={{
              borderColor: editorial ? c.rule2 : PALETTE.softBorder,
              color: editorial ? c.ink : PALETTE.slate,
            }}
          >
            ← Snapshots
          </button>
          {editorial ? (
            <span
              className="truncate text-base italic"
              style={{ color: c.ink, fontFamily: theme.fonts.serif }}
            >
              {snapshot.label}
            </span>
          ) : (
            <span className="truncate text-sm font-bold" style={{ color: PALETTE.ink }}>
              {snapshot.label}
            </span>
          )}
        </div>
        {editorial ? (
          <span
            data-testid="snapshot-instant-badge"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px]"
            style={{ backgroundColor: c.rollupTrack, color: c.slate, fontFamily: theme.fonts.mono }}
          >
            as it stood: {dateLabel}
          </span>
        ) : (
          <span
            data-testid="snapshot-instant-badge"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-bold"
            style={{ backgroundColor: AMBER_BG, borderColor: "#D97706", color: AMBER_INK }}
          >
            <span aria-hidden>⟲</span> as it stood: {dateLabel}
          </span>
        )}
      </div>

      {/* "as of <date>" banner — the calm, factual framing the rewind uses. */}
      {editorial ? (
        <div
          data-testid="snapshot-banner"
          className="border-b px-4 py-2 text-xs"
          style={{
            borderColor: "rgba(201,133,45,0.4)",
            backgroundColor: theme.stateStyle("blocked").chipBg,
            color: c.slate,
          }}
        >
          <span
            className="mr-2 text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.muted }}
          >
            SNAPSHOT
          </span>
          {`The map as it stood on ${dateLabel} — the moment this snapshot was pinned. Tasks added later are not shown.`}
        </div>
      ) : (
        <div
          data-testid="snapshot-banner"
          className="border-b px-4 py-1.5 text-[11px]"
          style={{ borderColor: "#D97706", backgroundColor: AMBER_BG, color: AMBER_INK }}
        >
          <span aria-hidden>● </span>
          {`The map as it stood on ${dateLabel} — the moment this snapshot was pinned. Tasks added later are not shown.`}
        </div>
      )}

      {/* The read-only canvas, fed the snapshot's node set. */}
      <div className="relative min-h-0 flex-1">
        {isError ? (
          <div
            data-testid="snapshot-unavailable"
            className="flex h-full flex-col items-center justify-center gap-1 text-center"
            style={{ backgroundColor: c.canvas, color: c.muted }}
          >
            <div className="text-sm font-semibold">This snapshot couldn’t be opened yet</div>
            <div className="text-xs" style={{ color: c.faint }}>
              The map’s history will be ready here soon.
            </div>
          </div>
        ) : isLoading ? (
          <div
            data-testid="snapshot-loading"
            className="flex h-full items-center justify-center"
            style={{ backgroundColor: c.canvas, color: c.muted }}
          >
            Opening the snapshot…
          </div>
        ) : hasNodes ? (
          <TaskCanvas
            readOnly
            roots={roots}
            members={members}
            collapsed={new Set()}
            selectedId={null}
            onSelect={() => undefined}
            onToggleCollapse={() => undefined}
            onSetState={() => undefined}
            onAddSubtask={() => undefined}
            onRename={() => undefined}
            onArchive={() => undefined}
            onAssignToggle={() => undefined}
            onOpenDrawer={() => undefined}
          />
        ) : (
          <div
            data-testid="snapshot-empty"
            className="flex h-full flex-col items-center justify-center gap-1 text-center"
            style={{ backgroundColor: c.canvas, color: c.muted }}
          >
            <div className="text-sm font-semibold">Nothing on the map at this snapshot</div>
            <div className="text-xs" style={{ color: c.faint }}>
              The plan hadn’t been started when this moment was pinned.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
